import { type i18n } from "i18next";

export function normalizeValidationErrors (list_errors?: ApiErrorMessageObject) {
    const messages: { field: string; message: string }[] = [];

    const appendValidationMessage = (field: string, value: ApiErrorMessage) => {
        if (typeof value === "string") {
            messages.push({
                field,
                message: value,
            });
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => appendValidationMessage(field, item));
            return;
        }

        if (typeof value === "object" && value !== null) {
            Object.values(value).forEach((item) => appendValidationMessage(field, item));
        }
    };

    Object.entries(list_errors || {}).forEach(([field, value]) => {
        appendValidationMessage(field, value);
    });

    return messages;
};


export const pad2 = (value: string | number) => String(value).padStart(2, "0");

const normalizeDateStringCandidates = (value: string | number | Date) => {
    const trimmed = `${value || ""}`.trim();
    if (!trimmed) return [];

    const candidates = new Set([trimmed]);

    const addCandidate = (candidate) => {
        if (!candidate) return;
        const normalized = `${candidate}`.trim();
        if (!normalized) return;
        candidates.add(normalized);
    };

    const baseWithoutOffsetSpace = trimmed.replace(/\s+([+-]\d{2}:?\d{2}|Z)$/i, "$1");
    addCandidate(baseWithoutOffsetSpace);

    const seedValues = Array.from(candidates);
    seedValues.forEach((seedValue) => {
        addCandidate(seedValue.replace(/\.(\d{3})\d+/, ".$1"));
        addCandidate(seedValue.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
        addCandidate(seedValue.replace(/\s+/, "T"));
        addCandidate(seedValue.replace(/\s+/, "T").replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
        addCandidate(seedValue.replace(/\.(\d{3})\d+/, ".$1").replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
    });

    return Array.from(candidates);
};

export function parseDateValue (value: string | number | Date | null | undefined) {
    if (!value && value !== 0) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
    }

    if (typeof value === "number") {
        const parsedFromNumber = new Date(value);
        return Number.isNaN(parsedFromNumber.getTime()) ? null : parsedFromNumber;
    }

    if (typeof value === "string") {
        const candidates = normalizeDateStringCandidates(value);
        for (const candidate of candidates) {
            const parsedDate = new Date(candidate);
            if (!Number.isNaN(parsedDate.getTime())) {
                return parsedDate;
            }
        }

        const localDateMatch = value.trim().match(
            /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[T\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/
        );
        if (localDateMatch) {
            const [
                ,
                year,
                month,
                day,
                hours = "0",
                minutes = "0",
                seconds = "0",
            ] = localDateMatch;

            const parsedLocalDate = new Date(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hours),
                Number(minutes),
                Number(seconds),
                0
            );

            if (!Number.isNaN(parsedLocalDate.getTime())) {
                return parsedLocalDate;
            }
        }
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function toDateInputString (value: string | number | Date) {
    if (!value) return "";

    if (typeof value === "string") {
        const trimmed = value.trim();
        const dateMatch = trimmed.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
        if (dateMatch) {
            return `${dateMatch[1]}-${pad2(dateMatch[2])}-${pad2(dateMatch[3])}`;
        }
    }

    const parsedDate = parseDateValue(value);
    if (!parsedDate) return "";

    return `${parsedDate.getFullYear()}-${pad2(parsedDate.getMonth() + 1)}-${pad2(parsedDate.getDate())}`;
};

export function toTimeString (value: string | number | Date) {
    if (!value) return "";
    if (typeof value === "string") {
        const trimmed = value.trim();
        const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (match) {
            const hours = String(match[1]).padStart(2, "0");
            const minutes = match[2];
            return `${hours}:${minutes}`;
        }
    }

    const parsedDate = parseDateValue(value);
    if (!parsedDate) {
        if (typeof value === "string") {
            const fallbackMatch = value.match(/(\d{1,2}):(\d{2})/);
            if (fallbackMatch) {
                const hours = String(fallbackMatch[1]).padStart(2, "0");
                const minutes = fallbackMatch[2];
                return `${hours}:${minutes}`;
            }
        }
        return "";
    }

    const hours = String(parsedDate.getHours()).padStart(2, "0");
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
};

// export function formatPickupDatetime (value, i18n) {
//     if (!value) return "";

//     const parsedDate = value instanceof Date ? value : new Date(value);
//     if (!Number.isNaN(parsedDate.getTime())) {
//         return new Intl.DateTimeFormat(i18n?.language || undefined, {
//             year: "numeric",
//             month: "short",
//             day: "2-digit",
//             hour: "2-digit",
//             minute: "2-digit",
//             hour12: false,
//         }).format(parsedDate);
//     }

//     return typeof value === "string" ? value : "";
// };

// export function formatDateByCountry(date, countryCode, fallback = null) {
//     if (!date) return fallback;
//     if (!new Date(date).toString() === "Invalid Date") return fallback;

//     const year = date.getFullYear();
//     const month = pad2(date.getMonth() + 1);
//     const day = pad2(date.getDate());
//     const hours = pad2(date.getHours());
//     const minutes = pad2(date.getMinutes());
//     const seconds = pad2(date.getSeconds());

//     const formats = {
//         DE: () => `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`,
//         AT: () => `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`,
//         GB: () => `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`,
//     };

//     if (typeof countryCode == "string" && formats[countryCode.toUpperCase()]) {
//         return formats[countryCode.toUpperCase()]();
//     }

//     // default ISO-like format
//     return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
// };

export function formatDatetimeByLocale(
    value: string | number | Date | undefined,
    i18n: i18n,
    options?: {
        timeZone?: string;
        includeSeconds?: boolean;
        numericMonth?: number;
        fallback?: string;
        fallbackToISO?: boolean;
    }
) {
    const {
        timeZone,            // Like 'Europe/Vienna' if you want to prove it
        includeSeconds = true,
        numericMonth = true, // If true => 2-digit month, if false => short (like Mar / Mär)
        fallback = "",
        fallbackToISO = true // If the parsing was invalid, fall back to ISO-like
    } = options || {};

    if (!value) return fallback;
    // if (!new Date(value).toString() === "Invalid Date") return fallback;

    // Parse robustly
    let parsedDate;
    if (value instanceof Date) {
        parsedDate = value;
    } else if (typeof value === "string") {
        const s = value.trim();

        // Support "YYYY-MM-DD HH:mm:ss" (for Safari/better compatibility)
        // Convert to "YYYY-MM-DDTHH:mm:ss"
        const normalized = s.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/)
            ? s.replace(" ", "T")
            : s;

        parsedDate = new Date(normalized);
    } else {
        parsedDate = new Date(value);
    }

    if (Number.isNaN(parsedDate.getTime())) {
        if (typeof value === "string") return value;
        return "";
    }

    const locale =
        i18n?.resolvedLanguage ||
        i18n?.language ||
        undefined;

    // If the locale was just "de" or "en" and you want to make it more precise, you can map it here.
    // (Optional)
    const resolvedLocale =
        locale === "de" ? "de-AT" :
        locale === "en" ? "en-GB" :
        locale;

    const dtf = new Intl.DateTimeFormat(resolvedLocale, {
        year: "numeric",
        month: numericMonth ? "2-digit" : "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: includeSeconds ? "2-digit" : undefined,
        hour12: false,
        timeZone: timeZone || undefined,
    });

    const formatted = dtf.format(parsedDate);

    // If you need the output to save/send (same fixed standard)
    if (!fallbackToISO) return formatted;

    return formatted;
};

export function formatDateWithTimezoneForApi (value: string | number | Date) {
    if (!value) return "";

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = pad2(date.getMonth() + 1);
    const day = pad2(date.getDate());
    const hour = pad2(date.getHours());
    const minute = pad2(date.getMinutes());
    const second = "00";

    const offsetMinutes = -date.getTimezoneOffset();
    const offsetSign = offsetMinutes >= 0 ? "+" : "-";
    const offsetAbs = Math.abs(offsetMinutes);
    const offsetHours = pad2(Math.floor(offsetAbs / 60));
    const offsetMins = pad2(offsetAbs % 60);

    return `${year}-${month}-${day}T${hour}:${minute}:${second}${offsetSign}${offsetHours}:${offsetMins}`;
};

export const buildDateWithTimezoneFromDateAndTimeForApi = (dateValue: string, timeValue: string | number | Date) => {
    const normalizedTime = toTimeString(timeValue);
    if (!dateValue || !normalizedTime) return "";

    const dateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!dateMatch) return "";

    const timeMatch = normalizedTime.match(/^(\d{2}):(\d{2})$/);
    if (!timeMatch) return "";

    const [, year, month, day] = dateMatch;
    const [, hour, minute] = timeMatch;

    const localDate = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        0,
        0
    );

    if (Number.isNaN(localDate.getTime())) return "";

    const offsetMinutes = -localDate.getTimezoneOffset();
    const offsetSign = offsetMinutes >= 0 ? "+" : "-";
    const offsetAbs = Math.abs(offsetMinutes);
    const offsetHours = pad2(Math.floor(offsetAbs / 60));
    const offsetMins = pad2(offsetAbs % 60);

    return `${year}-${month}-${day}T${hour}:${minute}:00${offsetSign}${offsetHours}:${offsetMins}`;
};


export const getNormalizedPrice = (val: string | number) => {
    const parsedValue = parseFloat(val.toString());
    return Number.isFinite(parsedValue)
        ? Math.max(0, parsedValue).toFixed(2)
        : '';
};

export const getNormalizedCount = (val: string | number) => {
    const parsedValue = parseInt(val.toString(), 10);
    return Number.isFinite(parsedValue)
        ? Math.max(0, parsedValue)
        : null;
};

export const getNormalizedTax = (val: string | number, unit?: string) => {
    const parsedValue = parseFloat(val.toString());
    return (
        Number.isFinite(parsedValue) && (unit || "percent") === "percent"
            ? Math.min(Math.max(0, parsedValue), 100) :
        Number.isFinite(parsedValue)
            ? Math.max(0, parsedValue)
            : null
    );
};
